import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router';
import { isSafari, isIOS } from 'react-device-detect';
import { Switch, FormControlLabel, Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { resetInputMap, updateModalVisibility } from 'state/actions/action-creators';
import { translate, setLocale, getLocale } from 'translations/translator';
import SheetEditor from './sheet-editor';
import TeachingTipsModal from './teaching-tips-modal';
import SheetFooter from './sheet-footer';
import { parseQueryParams } from 'utils/query-param-util';
import { toBoolean } from 'utils/string-util';

const t = key => translate('worksheet', key);
const tAria = key => translate('a11y', key);

const SheetContainer = () => {
  const classes = useStyles();
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [language, setLanguage] = useState('EN_US');

  const printRef = useRef(null);

  const isLite = toBoolean(parseQueryParams(location.search)?.lite);
  const sectionId = +params?.sectionId!;
  const sheetId = +params?.sheetId!;

  const landscapeSheets = [
    0, 1, 2, 3, 4, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 26, 27, 52, 53, 54, 55, 171, 173, 380
  ];
  const isLandscape = landscapeSheets.includes(sheetId);

  const { wrapper, container, header, headerIcon, backButton, content, footer, languageToggle } =
    classes;
  const teachingTipsModalVisible = useSelector(
    (state: any) => state.worksheetMaker.teachingTipsModalVisible
  );

  const handleToggle = () => {
    const otherLanguage = language === 'ES_US' ? 'EN_US' : 'ES_US';
    setLanguage(otherLanguage);
    setLocale(otherLanguage);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');

    // Detect iOS/iPadOS
    if (printWindow && printRef.current) {
      // Collect all <link> and <style> tags from the main document's <head>
      const headLinksAndStyles = Array.from(
        document.head.querySelectorAll('link[rel="stylesheet"], style')
      )
        .map(node => node.outerHTML)
        .join('\n');

      // Collect all accessible CSS rules as a fallback
      const styles = Array.from(document.styleSheets)
        .map(styleSheet => {
          try {
            if (styleSheet.cssRules) {
              return Array.from(styleSheet.cssRules)
                .map(rule => rule.cssText)
                .join('\n');
            }
            return null;
          } catch (e) {
            return null;
          }
        })
        .filter(Boolean)
        .join('\n');

      const contentToPrint = (printRef.current as any).cloneNode(true) as Node;

      // Set up the new document with all styles and links
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Print</title>
            ${headLinksAndStyles}
            <style>
              ${styles}
              #print-container {
                 height: 100%;
                  width: 100%;
                  margin: 0;
                  padding: 0; 
              }
              body { margin: 0; padding: 0; }
              @media print {
                body { -webkit-print-color-adjust: exact;
                  height: ${isLandscape ? 850 : 1100}px;
                  width: ${isLandscape ? 1100 : 850}px;
                }
              }
            </style>
          </head>
          <body>
            <div id="print-container"></div>
          </body>
        </html>
      `);

      // Append your content
      const container = printWindow.document.getElementById('print-container');
      if (container) {
        container.appendChild(contentToPrint);
      }

      // Trigger print after content is loaded
      printWindow.document.close();

      printWindow.onload = function () {
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
        }, 300);

        // Try to close after printing (most browsers)
        printWindow.onafterprint = function () {
          if (isIOS && isSafari) printWindow.alert(t('closePrintWindowMessage'));
          printWindow.close();
        };

        // Fallback: force close after a delay (if onafterprint fails)
        setTimeout(() => {
          try {
            printWindow.close();
          } catch (e) {
            // ignore
          }
        }, 2000);
      };
    } else {
      console.error('Print reference is null or window could not be opened');
    }
  };

  const handleBackKeyPress = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      navigate(`/${sectionId}${location.search}`);
    }
  };

  const handleBackClick = () => {
    dispatch(resetInputMap());
    navigate(-1);
  };

  useEffect(() => {
    const locale = getLocale();
    setLanguage(locale);
  }, [language]);

  return (
    <Box className={wrapper}>
      <TeachingTipsModal
        teachingTipsModalVisible={teachingTipsModalVisible}
        onCloseModal={() => dispatch(updateModalVisibility(false))}
      />
      <Box className={container} displayPrint={'none'}>
        <Box className={header} displayPrint={'none'}>
          <Box
            className={backButton}
            onKeyPress={handleBackKeyPress}
            tabIndex={0}
            role="button"
            aria-label={tAria('worksheetBackButton')}
            onClick={handleBackClick}
          >
            <FontAwesomeIcon icon={faArrowLeft} className={headerIcon} />
            {t('backToSelector')}
          </Box>
          <Box className={languageToggle}>
            <FormControlLabel
              sx={{
                height: 'max-content',
                '@media (max-width: 400px)': {
                  flexDirection: 'column',
                  '& .MuiFormControlLabel-label': {
                    fontSize: '8px'
                  }
                }
              }}
              control={
                <Switch
                  defaultChecked
                  disableRipple
                  checked={language === 'ES_US'}
                  onChange={handleToggle}
                  inputProps={{ 'aria-label': t('switchLanguage') }}
                  color="primary"
                  sx={{
                    '& .MuiSwitch-input:focus + .MuiSwitch-thumb': { outline: '3px solid #005fcc' }
                  }}
                />
              }
              label="Español"
            />
          </Box>
        </Box>
        <Box className={`${content} content-container`}>
          <SheetEditor
            isLite={isLite}
            sectionId={sectionId}
            sheetId={sheetId}
            language={language}
            printRef={printRef}
          />
        </Box>
        <Box className={footer}>
          <SheetFooter isLite={isLite} sectionId={sectionId} handlePrint={handlePrint} />
        </Box>
      </Box>
    </Box>
  );
};

export default SheetContainer;

const useStyles = makeStyles({
  wrapper: {
    height: '100%',
    paddingTop: 26,
    paddingBottom: 26,
    overflow: 'auto',
    '@media (max-width: 1100px)': {
      paddingTop: 0,
      paddingBottom: 0
    }
  },
  container: {
    margin: 'auto',
    width: '90%',
    height: '100%',
    border: '1px solid #cccccc',
    borderRadius: 5,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'Futura-Semibold',
    position: 'relative'
  },
  header: {
    fontSize: '20px',
    display: 'flex',
    alignContent: 'space-between',
    borderBottom: '1px solid #cccccc',
    padding: '10px'
  },
  headerIcon: {
    marginRight: '10px',
    marginLeft: '20px',
    marginTop: '4px',
    cursor: 'pointer'
  },
  backButton: {
    color: '#2e7bc2',
    '&:hover': {
      cursor: 'pointer',
      textDecoration: 'underline'
    },
    '@media (max-width: 600px)': {
      fontSize: '14px'
    }
  },
  languageToggle: {
    position: 'absolute',
    right: 90,
    '@media (max-width: 600px)': {
      position: 'static',
      padding: '0 0 0 10px'
    }
  },
  content: {
    width: '100%',
    minHeight: '500px',
    overflow: 'auto',
    display: 'flex',
    backgroundColor: '#EEEEEE',
    flex: 1
  },
  footer: {
    width: '100%',
    borderTop: '1px solid #cccccc',
    padding: '10px'
  }
});
